import React, { useRef, useEffect, useState } from "react";
import {
    select,
    axisBottom,
    scaleLinear,
    scaleOrdinal,
    schemeCategory10
} from "d3";
import { useResizeObserver } from "../../utility/useResizeObserver";
import { fixCountryName } from "../../utility/formatCountry";
import "d3-transition";

export const StackedBarChart = ({
    data,
    setTitle,
    year,
    country,
    property,
    hidden,
    selectedTypology: propSelectedTypology,
    onTypologyChange
}) => {
    const barRef = useRef(null);
    const xAxisRef = useRef(null);
    const legendRef = useRef(null);
    const tooltipRef = useRef(null);
    const wrapperRef = useRef(null);

    // "All" or specific typology
    const [selectedTypology, setSelectedTypology] = useState(propSelectedTypology || "All");
    const [typologyArray, setTypologyArray] = useState([]);

    const [chartHeight, setChartHeight] = useState('120vh');

    // We'll store the chart title in local state
    const [pinnedTitle, setPinnedTitle] = useState("");

    // Basic margins
    const margin = { top: 10, right: 50, bottom: 100, left: 150 };

    // Size of each stacked bar segment
    const subBandHeight = 20;
    const subBandSpacing = 10; // spacing between countries
    const yearSpacing = 20; // extra spacing after each year block

    // Use a custom hook to measure #chartContainer
    const dimensions = useResizeObserver(wrapperRef);

    // 1) Collect typologies from your data
    useEffect(() => {
        if (!data) return;
        const flatData = data.flat().filter(Boolean);
        const setTypos = new Set();
        flatData.forEach((entry) => {
            // For stacked usage, we ignore "Year" and "Country"
            Object.keys(entry).forEach((k) => {
                if (k !== "Country" && k !== "Year") {
                    setTypos.add(k);
                }
            });
        });
        const newTypos = Array.from(setTypos);
        setTypologyArray(newTypos);

        // Ensure selectedTypology is valid
        if (!newTypos.includes(selectedTypology) && selectedTypology !== "All") {
            setSelectedTypology("All");
        }
    }, [data, selectedTypology]);

    // Handle prop changes for export
    useEffect(() => {
        if (propSelectedTypology && propSelectedTypology !== selectedTypology) {
            setSelectedTypology(propSelectedTypology);
        }
    }, [propSelectedTypology, selectedTypology]);

    // 2) Main chart rendering
    useEffect(() => {
        if (!data || !dimensions) return;

        const flatData = data.flat().filter(Boolean);
        if (flatData.length === 0) {
            select(barRef.current).selectAll("*").remove();
            select(xAxisRef.current).selectAll("*").remove();
            setPinnedTitle("No data available");
            return;
        }

        // Determine keys for stacking
        let keys;
        if (selectedTypology === "All") {
            keys = typologyArray;
        } else {
            keys = [selectedTypology];
        }

        // Group data by Year -> Country
        const nestedData = {};
        flatData.forEach((row) => {
            const yval = row.Year;
            if (!yval) return;
            const rawCountry = row.Country || "";
            const ctry = fixCountryName(rawCountry);

            if (!nestedData[yval]) {
                nestedData[yval] = {};
            }
            if (!nestedData[yval][ctry]) {
                nestedData[yval][ctry] = { Year: yval, Country: ctry };
                keys.forEach((k) => {
                    nestedData[yval][ctry][k] = 0;
                });
            }
            keys.forEach((k) => {
                nestedData[yval][ctry][k] += parseFloat(row[k]) || 0;
            });
        });

        // Sort years ascending
        const allYears = Object.keys(nestedData)
            .map((d) => +d)
            .sort((a, b) => a - b);
        const years = allYears.map(String);

        // Build array for each year, then sort countries by total
        const dataByYear = {};
        years.forEach((yr) => {
            const cObjs = Object.values(nestedData[yr]);
            cObjs.sort((a, b) => {
                const sumA = keys.reduce((acc, k) => acc + (a[k] || 0), 0);
                const sumB = keys.reduce((acc, k) => acc + (b[k] || 0), 0);
                return sumB - sumA; // big to small
            });
            dataByYear[yr] = cObjs;
        });

        const { width, height } = dimensions;
        if (width < 50 || height < 50) return;

        // Compute total "virtual" height
        let totalChartHeight = 0;
        years.forEach((yr) => {
            const cCount = dataByYear[yr].length;
            totalChartHeight += cCount * (subBandHeight + subBandSpacing) + yearSpacing;
        });
        totalChartHeight = Math.max(0, totalChartHeight - yearSpacing);

        // Add extra space for legend and disclaimer when hidden (for export)
        const extraSpace = hidden ? 300 : 100; // Much more space for export version
        setChartHeight(totalChartHeight + extraSpace + 'px');


        // Numeric max for X
        let xMax = 0;
        years.forEach((yr) => {
            dataByYear[yr].forEach((row) => {
                const sum = keys.reduce((acc, k) => acc + (row[k] || 0), 0);
                if (sum > xMax) xMax = sum;
            });
        });

        const xScale = scaleLinear()
            .domain([0, xMax])
            .nice()
            .range([0, width - margin.left - margin.right]);

        // Color scale for keys
        const colorScale = scaleOrdinal(keys, schemeCategory10);

        // Draw bars
        drawHorizontalBars({
            barRef,
            years,
            dataByYear,
            keys,
            xScale,
            colorScale,
            margin,
            width,
            totalChartHeight,
            subBandHeight,
            subBandSpacing,
            yearSpacing,
            tooltipRef,
            wrapperRef,
            hidden
        });

        // Pinned axis
        drawPinnedXAxis({ xAxisRef, xScale, width, margin });

        // Legend
        drawLegend({ legendRef, keys, colorScale, barRef });
    }, [data, dimensions, selectedTypology, typologyArray, propSelectedTypology]);

    // 3) Dynamic chart title
    useEffect(() => {
        const newLabel = getDynamicBarTitle(
            selectedTypology,
            property,
            year,
            country
        );
        setTitle(newLabel);
        setPinnedTitle(newLabel);
    }, [selectedTypology, property, year, country, setTitle]);

    return (
        <>
            <div className="chartTitle">
                {/* A dropdown for "All" or single typology */}
                {typologyArray.length > 0 && !hidden && (
                    <select
                        className="input"
                        id="dataCategoriesBar"
                        value={selectedTypology}
                        onChange={(e) => {
                            setSelectedTypology(e.target.value);
                            if (onTypologyChange) {
                                onTypologyChange(e.target.value);
                            }
                        }}
                    >
                        <option value="All">All Categories</option>
                        {typologyArray.map((t) => (
                            <option key={t} value={t}>
                                {t}
                            </option>
                        ))}
                    </select>
                )}
                <p>{pinnedTitle}</p>
                <p id="subtitle">
                    Please note that the following values refer to the projects and plants
                    described in detail in the WGC reporting. They may overlook project
                    data and fail to achieve the actual Grand Total values
                </p>
            </div>

            <div
                ref={wrapperRef}
                id="chartContainer"
                style={{ position: "relative", height: hidden ? chartHeight : "100%" }}
            >
                <div ref={legendRef} id="legendPile" className={hidden ? "hiddenLegend" : ""} ></div>

                <div
                    id="chartScroll"
                    style={{
                        position: "absolute",
                        top: hidden ? "0px" : "90px",
                        left: 0,
                        right: 0,
                        bottom: hidden ? "180px" : "30px",
                        overflowY: hidden ? "visible" : "auto"
                    }}
                >
                    <svg ref={barRef} className="chartSvg" />
                </div>

                <svg
                    ref={xAxisRef}
                    id="xAxisArea"
                    style={{
                        position: "absolute",
                        bottom: "0px",
                        left: 0,
                        right: 0,
                        height: "40px"
                    }}
                />
                <div ref={tooltipRef} />
            </div>
        </>
    );
};

// --------------- DRAWING FUNCTIONS ---------------

function drawHorizontalBars({
    barRef,
    years,
    dataByYear,
    keys,
    xScale,
    colorScale,
    margin,
    width,
    totalChartHeight,
    subBandHeight,
    subBandSpacing,
    yearSpacing,
    tooltipRef,
    wrapperRef,
    hidden
}) {
    const svg = select(barRef.current);
    svg.selectAll("*").remove();

    const chartHeight = totalChartHeight + margin.top + margin.bottom;
    svg.attr("width", width).attr("height", chartHeight);

    const g = svg.append("g").attr("transform", `translate(${margin.left},${margin.top})`);

    // Add vertical grid lines
    addVerticalGridLines(g, xScale, totalChartHeight);

    // For each "key," define a gradient
    const defs = svg.append("defs");
    keys.forEach((key, i) => {
        const grad = defs
            .append("linearGradient")
            .attr("id", `grad-key-${i}`)
            .attr("x1", "0%")
            .attr("x2", "100%")
            .attr("y1", "0%")
            .attr("y2", "0%");
        grad
            .append("stop")
            .attr("offset", "0%")
            .attr("stop-color", colorScale(key))
            .attr("stop-opacity", 0.8);

        grad
            .append("stop")
            .attr("offset", "100%")
            .attr("stop-color", colorScale(key))
            .attr("stop-opacity", 1);
    });

    // Build a tooltip selection
    const tooltip = select(tooltipRef.current)
        .style("opacity", 0)
        .style("visibility", "visible")
        .attr("class", "tooltip");

    let currentY = 0;

    years.forEach((yr) => {
        const cArray = dataByYear[yr];
        // Year group container
        const yearG = g
            .append("g")
            .attr("class", "yearGroup")
            .attr("transform", `translate(0,${currentY})`);

        // Label the year on the left
        yearG
            .append("text")
            .attr("x", -10)
            .attr("y", 0)
            .attr("text-anchor", "end")
            .attr("dominant-baseline", "hanging")
            .attr("fill", "#000")
            .text(`Year ${yr}`);

        let localY = 30; // Offset so year label isn't on top of bars

        // For each country row (inside cArray.forEach)
        cArray.forEach((row) => {
            // Compute stacked "segments"
            let x0 = 0;
            const segs = [];
            keys.forEach((k) => {
                const v = row[k] || 0;
                const x1 = x0 + v;
                segs.push({ key: k, x0, x1 });
                x0 = x1;
            });
            const total = x0; // Total value for this row

            const barG = yearG
                .append("g")
                .attr("class", "countryRow")
                .attr("transform", `translate(0,${localY})`);

            segs.forEach((seg) => {
                const segWidth = xScale(seg.x1) - xScale(seg.x0);
                if (segWidth <= 0) return;

                const pathD = roundedRectPath(
                    xScale(seg.x0),
                    0,
                    segWidth,
                    subBandHeight,
                    5
                );

                barG
                    .append("path")
                    .attr("class", "pileRect")
                    .attr("data-key", seg.key)
                    .attr("fill", `url(#grad-key-${keys.indexOf(seg.key)})`)
                    .attr("d", pathD)
                    .on("mouseover", function () {
                        select(this).transition().duration(150).style("opacity", 0.7);
                        tooltip
                            .style("opacity", 1)
                            .html(`${Number(row[seg.key] || 0).toFixed(2)}`);
                    })
                    .on("mousemove", (event) => {
                        const wrapperPos = wrapperRef.current.getBoundingClientRect();
                        tooltip
                            .style("left", `${event.clientX - wrapperPos.left + 10}px`)
                            .style("top", `${event.clientY - wrapperPos.top + 10}px`);
                    })
                    .on("mouseout", function () {
                        select(this).transition().duration(150).style("opacity", 1);
                        tooltip.style("opacity", 0);
                    })
                    .attr("stroke", "#fff")
                    .attr("stroke-width", 1)
                    .transition()
                    .duration(400)
                    .attrTween("d", function () {
                        const interpolateStart = xScale(seg.x0);
                        const interpolateEnd = xScale(seg.x0) + segWidth;
                        return function (t) {
                            const currentWidth =
                                interpolateStart + (interpolateEnd - interpolateStart) * t - xScale(seg.x0);
                            return roundedRectPath(
                                xScale(seg.x0),
                                0,
                                currentWidth,
                                subBandHeight,
                                5
                            );
                        };
                    });

                // *** Add segment value labels for export version ***
                if (hidden && segWidth > 20) {  // only if wide enough to display text
                    barG.append("text")
                        .attr("x", xScale(seg.x0) + segWidth / 2)
                        .attr("y", subBandHeight / 2)
                        .attr("dy", ".35em")
                        .attr("text-anchor", "middle")
                        .attr("fill", "#000")
                        .attr("font-size", "10px")
                        .text(row[seg.key] ? Number(row[seg.key]).toFixed(2) : "");
                }
            });

            // Add a text element showing the total sum on this row
            barG.append("text")
                .attr("x", xScale(total) + 5)
                .attr("y", subBandHeight / 2)
                .attr("dy", ".35em")
                .attr("fill", "#000")
                .attr("font-size", "10px")
                .text(total.toFixed(2));

            // Add the country label to the left
            barG.append("text")
                .attr("x", -10)
                .attr("y", subBandHeight / 2)
                .attr("font-size", "10px")
                .attr("text-anchor", "end")
                .attr("alignment-baseline", "middle")
                .text(row.Country)
                .call(wrapText, 100);

            localY += subBandHeight + subBandSpacing;
        });

        currentY += localY + yearSpacing;
    });
}

/** Minimal pinned X-axis. */
function drawPinnedXAxis({ xAxisRef, xScale, width, margin }) {
    const svgX = select(xAxisRef.current);
    svgX.selectAll("*").remove();

    svgX.attr("width", width).attr("height", 50);

    const axisG = svgX.append("g").attr("transform", `translate(${margin.left},10)`);
    const xAxis = axisBottom(xScale).ticks(5);
    axisG.call(xAxis);

    axisG
        .append("text")
        .attr("fill", "#FF7829")
        .attr("x", (width - margin.left - margin.right) / 2)
        .attr("y", 30)
        .attr("text-anchor", "middle");
}

/** Legend with hover highlight. */
function drawLegend({ legendRef, keys, colorScale, barRef }) {
    const legend = select(legendRef.current);
    legend.selectAll("*").remove();

    if (!keys || !keys.length) return;

    const items = legend
        .selectAll(".legend-item")
        .data(keys)
        .enter()
        .append("div")
        .attr("class", "legend-item")
        .style("display", "inline-flex")
        .style("alignItems", "center")
        .style("marginRight", "10px");

    items
        .append("div")
        .style("width", "12px")
        .style("height", "12px")
        .style("marginRight", "5px")
        .style("background", (d, i) => `url(#grad-key-${i})`)
        .style("background-color", (d) => colorScale(d))
        .style("border-radius", "3px");

    items.append("span").text((d) => d);

    // Legend hover
    items
        .on("mouseover", (event, typologyKey) => {
            select(barRef.current)
                .selectAll(".pileRect")
                .transition()
                .duration(200)
                .style("opacity", function () {
                    return select(this).attr("data-key") === typologyKey ? 1 : 0.2;
                });
        })
        .on("mouseout", () => {
            // Reset
            select(barRef.current)
                .selectAll(".pileRect")
                .transition()
                .duration(200)
                .style("opacity", 1);
        });
}

/** Add vertical grid lines behind bars (light dashed). */
function addVerticalGridLines(g, xScale, totalChartHeight) {
    const xTicks = xScale.ticks(5);
    g.selectAll(".vertical-grid")
        .data(xTicks)
        .join("line")
        .attr("class", "vertical-grid")
        .attr("x1", (d) => xScale(d))
        .attr("x2", (d) => xScale(d))
        .attr("y1", 0)
        .attr("y2", totalChartHeight)
        .attr("stroke", "#999")
        .attr("stroke-dasharray", "2,2")
        .attr("stroke-width", 0.5);
}

/** Helper: Draw a rounded rectangle path. */
function roundedRectPath(x, y, width, height, r) {
    const w = Math.max(0, width);
    const h = Math.max(0, height);
    const radius = Math.min(r, w / 2, h / 2);
    return `
    M${x},${y + radius}
    a${radius},${radius} 0 0 1 ${radius},-${radius}
    h${w - 2 * radius}
    a${radius},${radius} 0 0 1 ${radius},${radius}
    v${h - 2 * radius}
    a${radius},${radius} 0 0 1 -${radius},${radius}
    h-${w - 2 * radius}
    a${radius},${radius} 0 0 1 -${radius},-${radius}
    Z
  `;
}

/** Helper for text wrapping. */
function wrapText(text, maxWidth) {
    text.each(function () {
        const textEl = select(this),
            words = textEl.text().split(/\s+/).reverse(),
            lineHeight = 1.1;
        let word,
            line = [],
            lineNumber = 0;

        const x = textEl.attr("x"),
            y = textEl.attr("y"),
            dy = parseFloat(textEl.attr("dy") || 0);

        let tspan = textEl.text(null).append("tspan")
            .attr("x", x)
            .attr("y", y)
            .attr("dy", dy + "em");

        while ((word = words.pop())) {
            line.push(word);
            tspan.text(line.join(" "));
            if (tspan.node().getComputedTextLength() > maxWidth) {
                line.pop();
                tspan.text(line.join(" "));
                line = [word];
                tspan = textEl.append("tspan")
                    .attr("x", x)
                    .attr("y", y)
                    .attr("dy", ++lineNumber * lineHeight + dy + "em")
                    .text(word);
            }
        }
    });
}

/** Title logic: if the user picks a single typology, show it. */
function getDynamicBarTitle(selectedTypology, property, year, country) {
    const countryTitle = country
        .map((c, idx) => c.label + (idx === country.length - 1 ? "" : ", "))
        .join("");

    switch (property) {
        case "Pile":
            return `Gross Energy Produced (GWh/year) of ${selectedTypology} as declared for geothermal power plants (MWt) in ${countryTitle}`;
        case "Gross Energy Produced (GEP) (GWh/year)":
            return `Gross Energy Produced (GWh/year) of ${selectedTypology} as declared for geothermal power plants (MWt) in ${countryTitle}`;
        case "Installed Capacity (MW)":
            return `Installed Capacity of ${selectedTypology} as declared for geothermal power plants (MWt) in ${countryTitle}`;
        case "Energy used (net) (TJ/year)":
            return `Energy Used (TJ/year) of ${selectedTypology} as declared for geothermal Heating and Cooling categories in ${countryTitle}`;
        case "Nominal Capacity (MWt)":
            return `Installed Capacity (MWt) of ${selectedTypology} as declared for geothermal  Heating and Cooling categories in ${countryTitle}`;
        default:
            return `${property} of ${selectedTypology} in ${countryTitle}`;
    }
}